// Formatadores de datos para a UI.
//
// X-2.1.b: os formatters básicos (price/date/superficie) viven en `@leyro/i18n`
// para compartilos con mobile. As extensións específicas de web (deadline,
// status colors, relative time) quedan aquí.

export {
  HA_TO_FERRADOS,
  HA_TO_M2,
  haToFerrados,
  ferradosToHa,
  haToM2,
  m2ToHa,
  userUnitToHa,
  haToUserUnit,
  formatFerrados,
  formatPrice,
  formatDate,
} from "@leyro/i18n";

export const formatDateShort = (d) =>
  d ? new Date(d).toLocaleDateString("gl-ES", { day: "numeric", month: "short" }) : "—";

// Formatea un prazo absoluto + relativo, p.ex. "28 abr, 18:30 (36h restantes)".
// Pensado para countdown de sinatura de contrato. Devolve "Caducado" se xa
// pasou. Espera ISO string ou Date.
export const formatDeadline = (when) => {
  if (!when) return "—";
  const target = new Date(when);
  const ms = target.getTime() - Date.now();
  const absolute = target.toLocaleString("gl-ES", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  if (ms <= 0) return `${absolute} (caducado)`;
  const totalMin = Math.floor(ms / 60000);
  const hours = Math.floor(totalMin / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${absolute} (${days}d ${hours % 24}h restantes)`;
  if (hours >= 1) return `${absolute} (${hours}h ${totalMin % 60}min restantes)`;
  return `${absolute} (${totalMin}min restantes)`;
};

export const calcDays = (from, to) => {
  if (!from || !to) return 0;
  const diff = new Date(to) - new Date(from);
  return Math.max(1, Math.round(diff / 86400000));
};

export const formatRating = (r) =>
  r != null ? Number(r).toFixed(1) : null;

export const formatRelativeTime = (d) => {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "Agora mesmo";
  if (mins < 60) return `Hai ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `Hai ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `Hai ${days} días`;
  return formatDateShort(d);
};

// Cores por estado (a etiqueta resólvese vía i18n: trips:status.<STATUS>).
// Os consumidores usan: t(`trips:status.${status}`) + STATUS_COLORS[status].
export const STATUS_COLORS = {
  PENDING:          "bg-yellow-100 text-yellow-800",
  REQUEST_PENDING:  "bg-blue-100 text-blue-800",
  REQUEST_REJECTED: "bg-red-100 text-red-700",
  PAYMENT_HELD:     "bg-indigo-100 text-indigo-800",
  CONTRACT_PENDING: "bg-purple-100 text-purple-800",
  CONFIRMED:        "bg-green-100 text-green-800",
  ACTIVE:           "bg-green-100 text-green-800",
  COMPLETED:        "bg-gray-100 text-gray-700",
  DISPUTED:         "bg-orange-100 text-orange-800",
  CANCELLED:        "bg-red-100 text-red-700",
  IN_PROGRESS:      "bg-green-100 text-green-800",
  PENDING_CONTRACT: "bg-purple-100 text-purple-800",
};

export const STATUS_FALLBACK_COLOR = "bg-gray-100 text-gray-600";

// Helper para obter a etiqueta i18n + cor dun estado.
// Uso: const { label, color } = getStatusInfo(reservation.status, t);
export const getStatusInfo = (status, t) => ({
  label: t(`trips:status.${status}`, { defaultValue: status }),
  color: STATUS_COLORS[status] ?? STATUS_FALLBACK_COLOR,
});

// Compatibilidade con consumidores antigos (preferir getStatusInfo + i18n).
// Mantén etiquetas en galego para non romper imports existentes durante a
// migración progresiva a i18n.
const _LEGACY_GL_LABELS = {
  PENDING:          "Pendente",
  REQUEST_PENDING:  "Solicitude enviada",
  REQUEST_REJECTED: "Solicitude rexeitada",
  PAYMENT_HELD:     "Tarxeta autorizada",
  CONTRACT_PENDING: "Pendente de sinatura",
  CONFIRMED:        "Confirmada",
  ACTIVE:           "En curso",
  COMPLETED:        "Completada",
  DISPUTED:         "Con incidencia",
  CANCELLED:        "Cancelada",
  IN_PROGRESS:      "En curso",
  PENDING_CONTRACT: "Pendente contrato",
};
export const STATUSES_GL = Object.fromEntries(
  Object.keys(_LEGACY_GL_LABELS).map((k) => [
    k,
    { label: _LEGACY_GL_LABELS[k], color: STATUS_COLORS[k] },
  ])
);
